import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import dayjs from 'dayjs';
import { VoIPHistoryRecord } from '../utils/voipHistoryService';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface VoIPHistoryGraphProps {
  data: VoIPHistoryRecord[];
  deviceName?: string;
}

const VoIPHistoryGraph: React.FC<VoIPHistoryGraphProps> = ({ data, deviceName }) => {
  // Process data for charts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    // Sort data by timestamp
    const sortedData = [...data].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Group data by hour for timeline
    const hourlyData: Map<
      string,
      { online: number; offline: number; total: number; latencySum: number; latencyCount: number }
    > = new Map();

    sortedData.forEach((record) => {
      const hourKey = dayjs(record.timestamp).format('DD/MM HH:00');
      const existing = hourlyData.get(hourKey) || {
        online: 0,
        offline: 0,
        total: 0,
        latencySum: 0,
        latencyCount: 0,
      };
      existing.total++;
      if (record.status === 'online') {
        existing.online++;
      } else {
        existing.offline++;
      }
      if (record.latency > 0) {
        existing.latencySum += record.latency;
        existing.latencyCount++;
      }
      hourlyData.set(hourKey, existing);
    });

    const labels = Array.from(hourlyData.keys());
    const onlineData = Array.from(hourlyData.values()).map((v) => v.online);
    const offlineData = Array.from(hourlyData.values()).map((v) => v.offline);
    const uptimeData = Array.from(hourlyData.values()).map((v) =>
      v.total > 0 ? (v.online / v.total) * 100 : 0
    );
    const latencyData = Array.from(hourlyData.values()).map((v) =>
      v.latencyCount > 0 ? v.latencySum / v.latencyCount : 0
    );

    return {
      labels,
      onlineData,
      offlineData,
      uptimeData,
      latencyData,
    };
  }, [data]);

  if (!chartData) {
    return (
      <div className="no-graph-data">
        <p>No data available for graph visualization.</p>
      </div>
    );
  }

  // Uptime Timeline Chart (Line)
  const uptimeChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Uptime %',
        data: chartData.uptimeData,
        fill: true,
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: '#22c55e',
      },
    ],
  };

  const uptimeOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: `Device Uptime Over Time${deviceName ? ` - ${deviceName}` : ''}`,
        font: { size: 14, weight: 'bold' },
      },
      tooltip: {
        callbacks: {
          label: (context) => `Uptime: ${(context.raw as number)?.toFixed(1)}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Uptime %',
        },
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
      x: {
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  // Latency Chart (Line)
  const latencyChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Avg Latency (ms)',
        data: chartData.latencyData,
        fill: true,
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: '#6366f1',
      },
    ],
  };

  const latencyOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: 'Average Latency Over Time',
        font: { size: 14, weight: 'bold' },
      },
      tooltip: {
        callbacks: {
          label: (context) => `Latency: ${(context.raw as number)?.toFixed(1)} ms`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Latency (ms)',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  // Status Distribution Chart (Bar)
  const statusBarData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Online',
        data: chartData.onlineData,
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderColor: '#22c55e',
        borderWidth: 1,
      },
      {
        label: 'Offline',
        data: chartData.offlineData,
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: '#ef4444',
        borderWidth: 1,
      },
    ],
  };

  const statusBarOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: 'Status Distribution Over Time',
        font: { size: 14, weight: 'bold' },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: true,
        title: {
          display: true,
          text: 'Count',
        },
      },
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  // Calculate summary stats
  const totalRecords = data.length;
  const onlineRecords = data.filter((r) => r.status === 'online').length;
  const offlineRecords = data.filter((r) => r.status === 'offline').length;
  const avgLatency =
    data.filter((r) => r.latency > 0).reduce((sum, r) => sum + r.latency, 0) /
      (data.filter((r) => r.latency > 0).length || 1) || 0;

  return (
    <div className="history-graphs">
      <div className="graph-grid">
        <div className="graph-card">
          <div className="graph-container" style={{ height: '280px' }}>
            <Line data={uptimeChartData} options={uptimeOptions} />
          </div>
        </div>
        <div className="graph-card">
          <div className="graph-container" style={{ height: '280px' }}>
            <Line data={latencyChartData} options={latencyOptions} />
          </div>
        </div>
        <div className="graph-card full-width">
          <div className="graph-container" style={{ height: '280px' }}>
            <Bar data={statusBarData} options={statusBarOptions} />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="graph-summary">
        <div className="summary-item">
          <span className="summary-label">Total Records</span>
          <span className="summary-value">{totalRecords}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Online States</span>
          <span className="summary-value success">{onlineRecords}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Offline States</span>
          <span className="summary-value danger">{offlineRecords}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Avg Uptime</span>
          <span className="summary-value">
            {totalRecords > 0 ? ((onlineRecords / totalRecords) * 100).toFixed(1) : 0}%
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Avg Latency</span>
          <span className="summary-value">{avgLatency.toFixed(1)} ms</span>
        </div>
      </div>
    </div>
  );
};

export default VoIPHistoryGraph;
